import { Collection, Database, Q } from '@nozbe/watermelondb';

import Contact from './Contact';
import Entry from './Entry';
import EntryProcedure from './EntryProcedure';
import Horse from './Horse';
import Organisation from './Organisation';
import Procedure from './Procedure';
import {
    InvoiceModel,
    InvoicePayload,
    PreparedDataOnInvoiceAdd,
    PreparedDataOnInvoiceDelete,
    PreparedDataOnInvoiceUpdate,
} from '../../types/Invoice';
import { InvoicesFiltersObject } from '../../types/invoicesFilters';
import { calculateInvoiceDetailsFromItems } from '../../utils/invoicing';
import { InvoiceDBServiceOptions } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import {
    applyInvoicesFiltersQueries,
    doesContactMatchSearchFilter,
    doesNamePartMatchSearchFilter,
} from '../utils';
import { getLocal, getNowISO } from '../../utils/date';
import { getProceduresFromEntriesIds } from '../../utils/procedures';
import { Queries } from '../../types/watermelonDb';
import { INVOICE_STATUS } from '../../constants/invoices/statuses';
import { ContactModel } from '../../types/Contacts';
import { OrganisationAccountingsModel } from '../../types/OrganisationAccountings';
import { FILTER_TYPE } from '../../types/filter';
import { AccountingProviderType } from '../../types/Invoicing';
import InvoiceInvoicingProduct from '../models/InvoiceInvoicingProduct';
import { getMonthBoundaries } from '../../utils/dateUtils';
import { addRelatedDataBatch } from './helpers/invoice/common';
import {
    appendPreparedBumpEntryProcedures,
    appendPreparedBumpHorses,
    appendPreparedContact,
    appendPreparedInvoiceEntries,
    appendPreparedInvoiceProducts,
    prepareBumpUpdates,
    prepareInvoiceContactUpdate,
    prepareInvoiceEntryUpdates,
    fetchBumpElements,
    fetchContactElement,
    fetchInvoiceEntryElements,
    InvoiceAddDataToBump,
    logDBActions,
    prepareCreateInvoice,
    prepareInvoiceProducts,
} from './helpers/invoice/invoiceAdd';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    appendPreparedBumpContact,
    appendPreparedBumpEntries,
    fetchBumpRelatedElements,
    logBumpRelatedDBActions,
    prepareBumpRelatedUpdates,
} from './helpers/invoice/invoiceBump';
import {
    appendPreparedInvoice,
    appendPreparedInvoiceUpdateEntries,
    appendPreparedInvoiceUpdateProducts,
    logUpdateDBActions,
    prepareInvoiceUpdateEntries,
    prepareInvoiceUpdateProducts,
    prepareUpdateInvoice,
} from './helpers/invoice/invoiceUpdate';
import {
    appendPreparedDeleteInvoice,
    appendPreparedDeleteInvoiceEntries,
    appendPreparedDeleteInvoiceEntryProcedures,
    appendPreparedDeleteInvoiceProducts,
    fetchDeleteInvoiceEntryProceduresData,
    logDeleteDBActions,
    prepareDeleteInvoiceRelatedData,
} from './helpers/invoice/invoiceDelete';

class Invoice {
    private database: Database;
    private collection: Collection<InvoiceModel>;
    private table = 'invoices';
    private options: InvoiceDBServiceOptions;
    private validation: DatabaseValidation;

    constructor(options: InvoiceDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getCount() {
        return this.collection.query().fetchCount();
    }

    observeCount() {
        return this.collection.query().observeCount();
    }

    // this method is used on the web, keep until unfinished drop of observables
    observeCreatedThisMonth() {
        const { start, end } = getMonthBoundaries();

        return this.collection
            .query(
                Q.and([
                    Q.where('created_at', Q.gte(start.getTime())),
                    Q.where('created_at', Q.lte(end.getTime())),
                ]),
            )
            .observeCount();
    }

    getCreatedThisMonthCount() {
        const { start, end } = getMonthBoundaries();

        return this.collection
            .query(
                Q.and([
                    Q.where('created_at', Q.gte(start.getTime())),
                    Q.where('created_at', Q.lte(end.getTime())),
                ]),
            )
            .fetchCount();
    }

    observeCountByProvider(provider: AccountingProviderType) {
        return this.collection
            .query(Q.where('provider', provider))
            .observeCount();
    }

    observeUnsyncedCount(provider: AccountingProviderType) {
        return this.collection
            .query(
                Q.where('provider', provider),
                Q.where('synced', Q.eq(false)),
            )
            .observeCount();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    private getInvoicesSearchLokiQuery(searchText: string) {
        const parsedSearchText = searchText.toLowerCase().trim();

        return [
            // Lack of types for Q.unsafeLokiTransform
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Q.unsafeLokiTransform((raws, loki) => {
                return raws.filter((rawRecord) => {
                    const contact = loki
                        .getCollection('contacts')
                        .by('id', rawRecord.contact_id);

                    if (contact) {
                        return (
                            doesContactMatchSearchFilter(
                                contact.first_name,
                                contact.last_name,
                                parsedSearchText,
                            ) ||
                            doesNamePartMatchSearchFilter(
                                contact.business_name ?? '',
                                searchText,
                            ) ||
                            doesNamePartMatchSearchFilter(
                                rawRecord.reference,
                                searchText,
                            )
                        );
                    } else {
                        doesNamePartMatchSearchFilter(
                            rawRecord.reference,
                            searchText,
                        );
                    }
                });
            }),
        ];
    }

    private getInvoicesSearchSQLQuery(searchText: string) {
        const parsedSearchText = searchText.toLowerCase().trim();

        const sanitizedSearch = Q.sanitizeLikeString(parsedSearchText);

        if (!sanitizedSearch.length) return;

        return [
            Q.experimentalJoinTables(['contacts']),
            Q.or(
                Q.on(
                    'contacts',
                    Q.or(
                        // @ts-ignore
                        Q.unsafeSqlExpr(
                            `first_name || ' ' || last_name LIKE '${sanitizedSearch}%'`,
                        ),
                        Q.where('last_name', Q.like(`${sanitizedSearch}%`)),
                        Q.where('business_name', Q.like(`${sanitizedSearch}%`)),
                        Q.where(
                            'business_name',
                            Q.like(`% ${sanitizedSearch}%`),
                        ),
                    ),
                ),
                Q.where('reference', Q.like(`${sanitizedSearch}%`)),
            ),
        ];
    }

    private getInvoicesFilterQueries(
        filters?: InvoicesFiltersObject,
        userHasProvider?: boolean,
    ) {
        const queries: Queries = [];

        if (filters) {
            queries.push(
                //@ts-ignore
                ...applyInvoicesFiltersQueries(filters, userHasProvider),
            );
        }

        return queries;
    }

    filterInvoices({
        searchText,
        filters,
        userHasProvider,
        filterType,
    }: {
        searchText: string;
        filters?: InvoicesFiltersObject;
        userHasProvider?: boolean;
        filterType: FILTER_TYPE;
    }) {
        const queries = this.getInvoicesFilterQueries(filters, userHasProvider);

        const searchQueries =
            filterType === FILTER_TYPE.LOKI
                ? this.getInvoicesSearchLokiQuery(searchText)
                : this.getInvoicesSearchSQLQuery(searchText);

        if (searchQueries) queries.push(...searchQueries);

        return this.collection
            .query(...queries)
            .extend(Q.sortBy('created_at', 'desc'));
    }

    getFilteredInvoicesCount(args: {
        searchText: string;
        filters?: InvoicesFiltersObject;
        userHasProvider?: boolean;
        filterType: FILTER_TYPE;
    }) {
        const query = this.filterInvoices(args);
        return query.fetchCount();
    }

    getFilteredInvoices(args: {
        searchText: string;
        filters?: InvoicesFiltersObject;
        userHasProvider?: boolean;
        filterType: FILTER_TYPE;
    }) {
        const query = this.filterInvoices(args);
        return query.fetch();
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    getByParams(params: { name: string; value: string }[]) {
        return this.collection.query(
            Q.and(...params.map((param) => Q.where(param.name, param.value))),
        );
    }

    fetchByParams(params: { name: string; value: string }[]) {
        return this.getByParams(params).fetch();
    }

    async bumpRelatedEntities(invoiceId: string) {
        const entryService = new Entry({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });
        const entryProcedureService = new EntryProcedure({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });
        const horseService = new Horse({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.options.validation,
        });
        const contactService = new Contact({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });

        const invoice = await this.getByID(invoiceId);

        await this.database.write(async () => {
            const bumpElements = await fetchBumpRelatedElements({
                invoiceId,
                entryService,
                entryProcedureService,
                contactService,
                contactId: invoice.contactId,
            });

            const bumpData = await prepareBumpRelatedUpdates({
                ...bumpElements,
                entryService,
                entryProcedureService,
                horseService,
                contactService,
            });

            let preparedData: PreparedDataOnInvoiceUpdate[] = [];

            preparedData = appendPreparedBumpEntries(preparedData, {
                entriesToBump: bumpData.entriesToBump,
            });
            preparedData = appendPreparedBumpEntryProcedures(preparedData, {
                entryProceduresToBump: bumpData.entryProceduresToBump,
                horsesToBump: bumpData.horsesToBump,
            });
            preparedData = appendPreparedBumpHorses(preparedData, {
                entryProceduresToBump: bumpData.entryProceduresToBump,
                horsesToBump: bumpData.horsesToBump,
            });
            preparedData = appendPreparedBumpContact(preparedData, {
                contactToBump: bumpData.contactToBump,
            });

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logBumpRelatedDBActions({
                entriesToBump: bumpData.entriesToBump,
                entryProceduresToBump: bumpData.entryProceduresToBump,
                contact: bumpData.contact,
                logDBAction: this.options.logger.logDBAction,
            });
        });
    }

    async add({
        payload,
        userId,
        dataToBump,
    }: {
        payload: InvoicePayload;
        userId: string;
        dataToBump?: InvoiceAddDataToBump;
    }) {
        const entryService = new Entry({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });
        const contactService = new Contact({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });
        const isInternalProvider = payload.provider === 'internal';

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const procedures = await getProceduresFromEntriesIds({
            entriesIds: payload.selectedEntriesIds,
            options: {
                database: this.database,
                imageService: this.options.imageService,
                logger: this.options.logger,
                validation: this.validation,
            },
        });

        return await this.database.write(async () => {
            const { subtotalSum, totalSum } =
                await calculateInvoiceDetailsFromItems({
                    procedures,
                    products: payload.selectedProducts.map((product) => ({
                        price: product.price,
                        quantity: product.quantity,
                    })),
                    taxRate: Number(payload.taxRate),
                    productsTaxRate: Number(payload.productsTaxRate),
                });

            const formattedTotal = totalSum.toFixed(2);
            const subtotalToCheck = subtotalSum.toFixed(2);

            const totalToCheck =
                payload.invoicingSystem && isInternalProvider
                    ? formattedTotal
                    : subtotalToCheck;

            const entryElements = await fetchInvoiceEntryElements({
                entryIds: payload.selectedEntriesIds,
                entryService,
            });
            const contactElement = payload.contactId
                ? await fetchContactElement({
                      contactId: payload.contactId,
                      contactService,
                  })
                : null;

            let bumpFetchedData:
                | Awaited<ReturnType<typeof fetchBumpElements>>
                | undefined;

            if (dataToBump) {
                const entryProcedureService = new EntryProcedure({
                    database: this.database,
                    imageService: this.options.imageService,
                    logDBAction: this.options.logger.logDBAction,
                    validation: this.validation,
                });
                const horseService = new Horse({
                    database: this.database,
                    imageService: this.options.imageService,
                    logger: this.options.logger,
                    validation: this.validation,
                });

                bumpFetchedData = await fetchBumpElements({
                    dataToBump,
                    entryProcedureService,
                    horseService,
                });
            }

            let preparedData: PreparedDataOnInvoiceAdd[] = [];

            const invoice = prepareCreateInvoice({
                payload,
                userId,
                organisationID,
                totals: { subtotalToCheck, totalToCheck },
                collection: this.collection,
            });
            preparedData.push({
                record: invoice,
                table: 'invoices',
                operation: 'add',
            });

            const entriesData = prepareInvoiceEntryUpdates({
                invoice,
                entryIds: payload.selectedEntriesIds,
                entryElements,
            });
            preparedData = appendPreparedInvoiceEntries(
                preparedData,
                entriesData,
            );

            const productsData = prepareInvoiceProducts({
                invoice,
                products: payload.selectedProducts,
                userId,
                organisationID,
                database: this.database,
            });
            preparedData = appendPreparedInvoiceProducts(
                preparedData,
                productsData,
            );

            let contactData:
                | ReturnType<typeof prepareInvoiceContactUpdate>
                | undefined;
            if (contactElement) {
                contactData = prepareInvoiceContactUpdate({
                    contactElement,
                });
                preparedData = appendPreparedContact(preparedData, contactData);
            }

            let preparedBumpData:
                | ReturnType<typeof prepareBumpUpdates>
                | undefined;
            if (bumpFetchedData) {
                preparedBumpData = prepareBumpUpdates(bumpFetchedData);
                preparedData = appendPreparedBumpEntryProcedures(
                    preparedData,
                    preparedBumpData,
                );
                preparedData = appendPreparedBumpHorses(
                    preparedData,
                    preparedBumpData,
                );
            }

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logDBActions({
                invoice,
                entriesData,
                contactData,
                preparedBumpData,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return invoice;
        }, 'create-invoice');
    }

    async update(id: string, payload: Partial<InvoicePayload>) {
        const entryService = new Entry({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });

        const invoiceProductsCollection =
            this.database.get<InvoiceInvoicingProduct>(
                'invoice_invoicing_products',
            );

        const initialProducts = invoiceProductsCollection
            ? await invoiceProductsCollection
                  .query(Q.where('invoice_id', id))
                  .fetch()
            : null;

        const invoiceElement = await this.getByID(id);
        const { selectedEntriesIds, selectedProducts } = payload;

        const entriesIds =
            selectedEntriesIds || (await invoiceElement.entries.fetch());
        const products = selectedProducts || initialProducts;

        const procedures = await getProceduresFromEntriesIds({
            entriesIds,
            options: {
                database: this.database,
                imageService: this.options.imageService,
                logger: this.options.logger,
                validation: this.validation,
            },
        });

        const { subtotalSum, totalSum } =
            await calculateInvoiceDetailsFromItems({
                procedures,
                products: products?.map((product) => ({
                    price: product.price,
                    quantity: product.quantity,
                })),
                taxRate: Number(payload.taxRate || invoiceElement.taxRate),
                productsTaxRate: Number(
                    payload.productsTaxRate || invoiceElement.productsTaxRate,
                ),
            });

        const totalToCheck = totalSum.toFixed(2);
        const subtotalToCheck = subtotalSum.toFixed(2);
        const initialEntries = await invoiceElement.entries.fetch();

        const updatedInvoice = await this.database.write(async () => {
            const updatedInvoiceElement = prepareUpdateInvoice({
                invoiceElement,
                payload,
                totals: { subtotalToCheck, totalToCheck },
            });

            let preparedData: PreparedDataOnInvoiceUpdate[] = [];
            preparedData = appendPreparedInvoice(
                preparedData,
                updatedInvoiceElement,
            );

            let entriesData:
                | Awaited<ReturnType<typeof prepareInvoiceUpdateEntries>>
                | undefined;
            if (selectedEntriesIds) {
                entriesData = await prepareInvoiceUpdateEntries({
                    invoiceId: invoiceElement.id,
                    selectedEntriesIds,
                    initialEntries,
                    entryService,
                });
                preparedData = appendPreparedInvoiceUpdateEntries(
                    preparedData,
                    entriesData,
                );
            }

            let productsData:
                | Awaited<ReturnType<typeof prepareInvoiceUpdateProducts>>
                | undefined;
            if (selectedProducts) {
                productsData = await prepareInvoiceUpdateProducts({
                    invoiceId: id,
                    selectedProducts,
                    initialProducts,
                    invoiceElement,
                    database: this.database,
                });
                preparedData = appendPreparedInvoiceUpdateProducts(
                    preparedData,
                    productsData,
                );
            }

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logUpdateDBActions({
                invoiceElement,
                entriesData,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return updatedInvoiceElement;
        }, 'update-invoice');

        return updatedInvoice;
    }

    async deleteByID(id: string) {
        const invoiceElement = await this.getByID(id);
        const entries = await invoiceElement.entries.fetch();
        const products = await invoiceElement.products.fetch();

        const procedureService = new Procedure({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });

        await this.database.write(async () => {
            const entryProcedurePairs =
                entries.length > 0
                    ? await fetchDeleteInvoiceEntryProceduresData({
                          entries,
                          procedureService,
                      })
                    : [];

            const deleteData = prepareDeleteInvoiceRelatedData({
                invoiceElement,
                entries,
                products,
                entryProcedurePairs,
            });

            let preparedData: PreparedDataOnInvoiceDelete[] = [];

            preparedData = appendPreparedDeleteInvoice(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteInvoiceEntries(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteInvoiceEntryProcedures(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteInvoiceProducts(
                preparedData,
                deleteData,
            );

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logDeleteDBActions({
                deleteData,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        }, 'delete-invoice');
    }

    getByStatusAndProvider({
        status,
        provider,
    }: {
        status: INVOICE_STATUS[];
        provider?: AccountingProviderType;
    }) {
        let queries = [Q.where('status', Q.oneOf(status))];

        if (provider) {
            queries.push(Q.where('provider', provider));
        }

        return this.collection.query(queries);
    }

    getAwaitingPaymentInvoices(provider?: AccountingProviderType) {
        return this.getByStatusAndProvider({
            status: [INVOICE_STATUS.authorised, INVOICE_STATUS.sent],
            provider,
        });
    }

    getDraftInvoices(provider?: AccountingProviderType) {
        return this.getByStatusAndProvider({
            status: [INVOICE_STATUS.draft],
            provider,
        });
    }

    getPaidInvoices(provider?: AccountingProviderType) {
        return this.getByStatusAndProvider({
            status: [INVOICE_STATUS.paid],
            provider,
        });
    }

    getOverdueInvoices(provider?: AccountingProviderType) {
        const today = getLocal();
        const formattedToday = Q.sanitizeLikeString(
            today.toISO().split('T')[0],
        ).replace(/_/g, '-');

        let queries = [
            Q.where(
                'status',
                Q.oneOf([INVOICE_STATUS.authorised, INVOICE_STATUS.sent]),
            ),
            Q.where('due_date_time', Q.lt(formattedToday)),
        ];

        if (provider) {
            queries.push(Q.where('provider', provider));
        }

        return this.collection.query(Q.and(queries));
    }

    async getArchivedInvoicesDetails(providers: AccountingProviderType[]) {
        const results: Partial<
            Record<
                AccountingProviderType,
                {
                    count: number;
                    notSynced: number;
                    lastUsedAt?: Date;
                    disconnectedAt?: Date;
                    accountId: string;
                }
            >
        > = {};

        for (const provider of providers) {
            if (provider === 'internal') {
                const invoices = await this.collection
                    .query(
                        Q.where('provider', provider),
                        Q.sortBy('updated_at', 'desc'),
                    )
                    .fetch();

                if (invoices.length) {
                    results[provider] = {
                        count: invoices.length,
                        notSynced: 0,
                        lastUsedAt: invoices[0].updatedAt,
                        accountId: 'internal',
                    };
                }
            } else {
                const invoices = await this.collection
                    .query(
                        Q.where('provider', provider),
                        Q.where('provider_archived_at', Q.notEq(null)),
                        Q.sortBy('provider_archived_at', 'desc'),
                    )
                    .fetch();

                const notSynced = await this.collection
                    .query(
                        Q.where('provider', provider),
                        Q.where('synced', Q.eq(false)),
                    )
                    .fetchCount();

                if (invoices.length) {
                    results[provider] = {
                        count: invoices.length,
                        notSynced,
                        disconnectedAt: invoices[0].providerArchivedAt
                            ? new Date(invoices[0].providerArchivedAt)
                            : undefined,
                        accountId: invoices[0].userProviderEmail || '',
                    };
                }
            }
        }

        return results;
    }

    async authoriseInvoice({
        invoiceId,
        contact,
        hasAccountingProvider,
    }: {
        invoiceId: string;
        contact?: Pick<ContactModel, 'email'> | null;
        hasAccountingProvider: boolean;
    }) {
        if (contact?.email && hasAccountingProvider) {
            await this.update(invoiceId, {
                status: INVOICE_STATUS.authorised,
                authorisedTime: getNowISO(),
                sentToContactTime: getNowISO(),
            });
        } else {
            await this.update(invoiceId, {
                status: INVOICE_STATUS.authorised,
                authorisedTime: getNowISO(),
            });
        }
    }

    setInvoiceToPaid(invoice: InvoiceModel) {
        return this.update(invoice.id, {
            status: INVOICE_STATUS.paid,
        });
    }

    async sendInvoice(
        invoice: Pick<InvoiceModel, 'id' | 'status'>,
        accountings: OrganisationAccountingsModel | null,
    ) {
        if (!accountings?.accountingProvider) return;

        const payload = { sentToContactTime: getNowISO() };

        if (
            invoice.status !== INVOICE_STATUS.authorised &&
            invoice.status !== INVOICE_STATUS.paid
        ) {
            payload['status'] = INVOICE_STATUS.authorised;
            payload['authorisedTime'] = getNowISO();
        }

        await this.update(invoice.id, payload);
    }

    getInvoicesUnsyncedWithProvider() {
        return this.collection.query(
            Q.or(Q.where('external_id', ''), Q.where('external_id', null)),
        );
    }

    getLastProviderSyncedInvoice(provider) {
        return this.collection.query(
            Q.where('provider', provider),
            Q.sortBy('last_synced_at', Q.desc),
            Q.take(1),
        );
    }

    getUnsyncedActiveInvoices(provider: AccountingProviderType) {
        return this.collection.query(
            Q.where('provider', provider),
            Q.where('synced', Q.eq(false)),
        );
    }

    getProviderTaxedDraftInvoices(provider: AccountingProviderType) {
        return this.collection.query(
            Q.where('provider', provider),
            Q.where('status', INVOICE_STATUS.draft),
            Q.where('apply_taxes', true),
        );
    }
}

export default Invoice;
